import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import {
  backgroundColor,
  border,
  borderColor,
  borderStyle,
  borderTop,
  borderWidth,
  display,
  flexDirection,
  height,
  maxHeight,
  space,
  width,
} from 'styled-system';

import { ResponsiveLazyImage } from 'common/components/LazyImage';
import type { ImageSizesWithAlt } from 'common/components/LazyImage/types';
import { Link } from 'common/components/Link';
import { BodyBoldBB2, HeadlineH5 } from 'common/components/Typography';
import { textWithEllipsis } from 'common/theme/styles';
import {
  borderRadiusBL,
  borderRadiusBR,
  flexGrow,
  textTransform,
} from 'common/theme/system-utilities';

export const SLIDE_WIDTH_S = 212;
export const SLIDE_WIDTH_M = 204;
export const SLIDE_WIDTH_L = 204;
export const SLIDE_WIDTH_XL = 292;

export const SLIDE_HEIGHT_S = 141;
export const SLIDE_HEIGHT_M = 136;
export const SLIDE_HEIGHT_L = 136;
export const SLIDE_HEIGHT_XL = 195;

const IMAGE_WIDTH = [
  `${SLIDE_WIDTH_S}px`,
  `${SLIDE_WIDTH_M}px`,
  `${SLIDE_WIDTH_L}px`,
  `${SLIDE_WIDTH_XL}px`,
];
const IMAGE_HEIGHT = [
  `${SLIDE_HEIGHT_S}px`,
  `${SLIDE_HEIGHT_M}px`,
  `${SLIDE_HEIGHT_L}px`,
  `${SLIDE_HEIGHT_XL}px`,
];

export interface ContentSliderItemProps {
  image: ImageSizesWithAlt;
  url: string;
  headline: string;
  subtitle: string;
}

const WrapperLink = styled(Link)<any>`
  ${display};
  ${flexDirection};
  ${height};
  ${width};
`;

const wrapperLinkStyles: ComponentProps<typeof WrapperLink> = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: IMAGE_WIDTH,
};

const Image = styled(ResponsiveLazyImage)<any>`
  ${width};
  ${height};
  ${display};
`;

const imageStyles: ComponentProps<typeof Image> = {
  width: 1,
  height: IMAGE_HEIGHT,
  display: 'block',
};

const ImagePlaceholder = styled.div<any>`
  ${width};
  ${height};
  ${backgroundColor};
`;

const imagePlaceholderStyles: ComponentProps<typeof ImagePlaceholder> = {
  width: 1,
  height: IMAGE_HEIGHT,
  backgroundColor: 'sys.neutral.background.active',
  'data-testid': 'image-placeholder',
};

const TextWrapper = styled.div<any>`
  ${space};
  ${borderRadiusBL};
  ${borderRadiusBR};
  ${borderStyle};
  ${borderWidth};
  ${borderColor};
  ${border};
  ${borderTop};
  ${flexGrow};
`;

const textWrapperStyles: ComponentProps<typeof TextWrapper> = {
  px: 'sp_16',
  py: ['sp_8', null, null, 'sp_16'],
  borderRadiusBL: 'small',
  borderRadiusBR: 'small',
  width: 1,
  borderStyle: 'solid',
  borderWidth: 'small',
  borderColor: 'sys.neutral.border.weak',
  borderTop: 'none',
  flexGrow: 1,
};

const Headline = styled(BodyBoldBB2)<any>`
  ${textTransform};
  ${textWithEllipsis(1)};
`;

const headlineStyles: ComponentProps<typeof Headline> = {
  color: 'sys.neutral.border.default',
  textTransform: 'uppercase',
};

const Subtitle = styled(HeadlineH5)<any>`
  ${textWithEllipsis(2)};
  ${maxHeight};
`;

const ContentSliderItem = ({ url, subtitle, headline, image }: ContentSliderItemProps) => (
  <WrapperLink {...wrapperLinkStyles} href={url} aria-label={subtitle}>
    {image ? (
      <Image {...imageStyles} {...image} />
    ) : (
      <ImagePlaceholder {...imagePlaceholderStyles} />
    )}
    <TextWrapper {...textWrapperStyles}>
      <Headline {...headlineStyles}>{headline}</Headline>
      <Subtitle>{subtitle}</Subtitle>
    </TextWrapper>
  </WrapperLink>
);

export default ContentSliderItem;
